import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loading } from 'react-daisyui'
import { useClient } from '@/stores/useClient.ts'
import { PortfolioViewResponse, PortfolioViewProject } from '@/models/profPortfolio.ts'
import { resolveTheme, HeyDriveType } from '@/models/drive.ts'
import { useEffect, useMemo } from 'react'
import ContactFooter from '@/components/ContactFooter.tsx'
import { StatScopeProvider, useTrackStat } from '@/contexts/statsScopeContext.tsx'

export function PortfolioPage() {
  const { portfolioId } = useParams<{ portfolioId: string }>()

  const statScope = useMemo(() => ({ source: 'portfolio' as const, portfolio: portfolioId }), [portfolioId])

  return (
    <StatScopeProvider scope={statScope}>
      <PortfolioPageContent />
    </StatScopeProvider>
  )
}

function PortfolioPageContent() {
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const { getPortfolioView } = useClient()
  const track = useTrackStat()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portfolio-view', portfolioId],
    queryFn: async () => {
      if (!portfolioId) return null
      const res = await getPortfolioView(portfolioId)
      return res.data as PortfolioViewResponse
    },
    enabled: !!portfolioId,
  })

  const theme = useMemo(() => {
    if (!data) return null
    // Portfolios are always prof — resolveTheme applies the default fallback
    return resolveTheme(data.theme_name, HeyDriveType.PROF)
  }, [data])

  // The portfolio overview was opened. The endpoint counts nothing itself
  // (the portal previews it), so it is reported from here.
  useEffect(() => {
    if (!portfolioId) return
    track('portfolio_view')
  }, [portfolioId])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-error">Portfolio nicht gefunden</p>
      </div>
    )
  }

  // Apply theme colors if available
  const bgColor = theme?.color_neutral || undefined
  const textColor = theme?.color_content || undefined
  const primaryColor = theme?.color_primary || undefined

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      {/* Header */}
      <div className="py-16 px-4 text-center">
        {data.thumbnail && (
          <img
            src={data.thumbnail}
            alt={data.title}
            className="w-24 h-24 rounded-full object-cover mx-auto mb-6"
          />
        )}
        <h1
          className="font-serif text-4xl md:text-5xl mb-4"
          style={{ color: primaryColor }}
        >
          {data.title}
        </h1>
        {data.description && (
          <p className="max-w-xl mx-auto opacity-80 whitespace-pre-wrap">
            {data.description}
          </p>
        )}
      </div>

      {/* Project Grid */}
      <div className="container mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.projects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              portfolioId={portfolioId!}
              primaryColor={primaryColor}
            />
          ))}
        </div>
        {data.projects.length === 0 && (
          <p className="text-center opacity-60 py-8">Keine Projekte vorhanden</p>
        )}
      </div>

      {data.contact && (
        <ContactFooter contact={data.contact} className='mt-auto !bg-transparent' />
      )}
    </div>
  )
}

interface ProjectCardProps {
  project: PortfolioViewProject
  portfolioId: string
  primaryColor?: string
}

function ProjectCard({ project, portfolioId, primaryColor }: ProjectCardProps) {
  return (
    <Link
      to={`/p/${portfolioId}/${project.id}`}
      className="group block overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow"
    >
      <div className="aspect-[4/3] bg-base-300 overflow-hidden">
        {project.thumbnail ? (
          <img
            src={project.thumbnail}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center opacity-30">
            <span className="text-4xl">📷</span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3
          className="font-semibold text-lg"
          style={{ color: primaryColor }}
        >
          {project.title}
        </h3>
        {project.event_date && (
          <p className="text-sm opacity-60 mt-1">
            {new Date(project.event_date).toLocaleDateString('de-DE', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        )}
      </div>
    </Link>
  )
}
